import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
  type RecaptchaVerifier,
} from "firebase/auth";
import { Button, Input, Label } from "@clevis/shared-ui";
import { auth } from "../lib/firebase";
import { newVerifier, twoStepMessage } from "../lib/twoStep";

// The second step of a sign-in: the 6-digit code from their authenticator
// app, or a code texted to their backup phone. Shown by the sign-in page (and
// when re-checking who you are before changing two-step itself).

export function TwoStepChallenge({ resolver, onDone, onCancel }: { resolver: MultiFactorResolver; onDone: () => void; onCancel: () => void }) {
  const hints = resolver.hints;
  const app = hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  const phone = hints.find((h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID) as PhoneMultiFactorInfo | undefined;
  const [method, setMethod] = useState<"app" | "text">(app ? "app" : "text");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const verifier = useRef<RecaptchaVerifier | null>(null);
  useEffect(() => () => verifier.current?.clear(), []);

  async function sendText() {
    if (!phone) return;
    setBusy(true);
    setError("");
    try {
      verifier.current ??= newVerifier("two-step-recaptcha");
      setVerificationId(await new PhoneAuthProvider(auth).verifyPhoneNumber({ multiFactorHint: phone, session: resolver.session }, verifier.current));
    } catch (err) {
      setError(twoStepMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const assertion =
        method === "app" && app
          ? TotpMultiFactorGenerator.assertionForSignIn(app.uid, code)
          : PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId ?? "", code));
      await resolver.resolveSignIn(assertion);
      onDone();
    } catch (err) {
      setError(twoStepMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-ink">Two-step sign-in</p>
        <p className="text-xs text-steel">
          {method === "app"
            ? "Enter the 6-digit code from your authenticator app for Clevis."
            : verificationId
              ? `Enter the code we texted to ${phone?.phoneNumber ?? "your phone"}.`
              : `We'll text a code to ${phone?.phoneNumber ?? "your phone"}.`}
        </p>
      </div>
      {method === "text" && !verificationId ? (
        <Button type="button" className="w-full" busy={busy} onClick={() => void sendText()}>
          Text me a code
        </Button>
      ) : (
        <>
          <div>
            <Label htmlFor="two-step-code">Code</Label>
            <Input
              id="two-step-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <Button type="submit" className="w-full" busy={busy} disabled={code.length !== 6}>
            Sign in
          </Button>
        </>
      )}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex justify-between text-xs">
        {app && phone ? (
          <button
            type="button"
            className="text-steel underline hover:text-ink"
            onClick={() => {
              setMethod(method === "app" ? "text" : "app");
              setCode("");
              setError("");
            }}
          >
            {method === "app" ? "No app? Text my backup phone" : "Use my authenticator app"}
          </button>
        ) : (
          <span className="text-steel">Lost your phone? Ask your company's owner to reset it.</span>
        )}
        <button type="button" className="text-steel hover:text-ink" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div id="two-step-recaptcha" />
    </form>
  );
}
